import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, deleteUser } from 'firebase/auth';
import { auth } from './firebase-config';
import styles from './Login.module.css';
import { collection, getDocs, query, where, doc, setDoc } from '@firebase/firestore'
import { db } from './firebase-config'

const USERNAME_RE = /^[A-Za-z0-9_]{3,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateUsername(raw) {
    const trimmed = (raw || '').trim();
    if (!trimmed) return 'Username is required.';
    if (trimmed.length < 3) return 'Username must be at least 3 characters.';
    if (trimmed.length > 20) return 'Username must be 20 characters or fewer.';
    if (!USERNAME_RE.test(trimmed)) return 'Username can only contain letters, numbers, and underscores.';
    return null;
}

function validateEmail(raw) {
    const trimmed = (raw || '').trim();
    if (!trimmed) return 'Email is required.';
    if (!EMAIL_RE.test(trimmed)) return 'Please enter a valid email address.';
    return null;
}

function validatePassword(raw) {
    if (!raw) return 'Password is required.';
    if (raw.length < 6) return 'Password must be at least 6 characters.';
    return null;
}

const Signup = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Inline touched-field validation so users see errors as they fix them
    const [touched, setTouched] = useState({ username: false, email: false, password: false });
    const usernameErr = touched.username ? validateUsername(username) : null;
    const emailErr = touched.email ? validateEmail(email) : null;
    const passwordErr = touched.password ? validatePassword(password) : null;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const uErr = validateUsername(username);
        const eErr = validateEmail(email);
        const pErr = validatePassword(password);
        if (uErr || eErr || pErr) {
            setTouched({ username: true, email: true, password: true });
            setError(uErr || eErr || pErr);
            return;
        }

        const trimmedUsername = username.trim();
        const trimmedEmail = email.trim();

        setSubmitting(true);
        try {
            // Firestore rules require an authenticated user to read backenddata,
            // so the auth user must be created BEFORE the username check. If the
            // username turns out to be taken, roll the auth user back.
            const userCredential = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
            const user = userCredential.user;

            try {
                const usernameQuery = query(
                    collection(db, 'backenddata'),
                    where('username', '==', trimmedUsername)
                );
                const existing = await getDocs(usernameQuery);
                if (!existing.empty) {
                    try { await deleteUser(user); } catch (_) { /* best-effort */ }
                    setError('Username is already taken.');
                    setSubmitting(false);
                    return;
                }

                await setDoc(doc(db, 'backenddata', user.uid), {
                    email: trimmedEmail,
                    username: trimmedUsername,
                    Friends: []
                });
            } catch (profileErr) {
                // Roll back the auth user if we fail to create their profile doc
                try { await deleteUser(user); } catch (_) { /* best-effort */ }
                setError('Could not complete signup. Please try again.');
                setSubmitting(false);
                return;
            }

            navigate('/', { replace: true });
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                setError('Email is already in use.');
            } else if (err.code === 'auth/weak-password') {
                setError('Password must be at least 6 characters.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else if (err.code === 'auth/network-request-failed') {
                setError('Network error. Check your connection and try again.');
            } else {
                setError(err.message || 'Signup failed. Please try again.');
            }
            setSubmitting(false);
        }
    }

    const inputStyle = (err) => err ? { borderColor: '#ff5577' } : undefined;
    const inlineErr = (msg) => msg ? <small style={{ color: '#ff5577', display: 'block', marginTop: 4 }}>{msg}</small> : null;

    return (<div>
        <main className={styles.container}>

            <section className={styles.section}>
                <div className={styles.formWrapper}>
                    <h1>Create a new account</h1>
                    {error && <p className={styles.error}>{error}</p>}
                    <form className={styles.form} noValidate>
                    <div className={styles.formGroup}>
                            <label htmlFor="username">Username</label>
                            <input
                                type="text"
                                id="username"
                                autoComplete="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                onBlur={() => setTouched(t => ({ ...t, username: true }))}
                                maxLength={20}
                                style={inputStyle(usernameErr)}
                                required
                            />
                            {inlineErr(usernameErr)}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email address</label>
                            <input
                                type="email"
                                id="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() => setTouched(t => ({ ...t, email: true }))}
                                style={inputStyle(emailErr)}
                                required
                            />
                            {inlineErr(emailErr)}
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onBlur={() => setTouched(t => ({ ...t, password: true }))}
                                minLength={6}
                                style={inputStyle(passwordErr)}
                                required
                            />
                            {inlineErr(passwordErr)}
                        </div>
                        <button
                            type="submit"
                            onClick={onSubmit}
                            disabled={submitting}
                            className={styles.custombutton}
                        >
                            {submitting ? 'Creating account…' : 'Sign up'}
                        </button>
                    </form>
                    <p>Already have an account? <NavLink to="/login">Log in</NavLink></p>
                </div>
            </section>
        </main></div>
    );
}

export default Signup;
